import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  currentRole,
  deleteCustomFieldDef,
  listCustomFieldDefs,
  saveCustomFieldDef,
} from '../../lib/outreach/api'
import {
  CUSTOM_FIELD_TYPES,
  hasOptions,
  parseOptions,
  slugifyKey,
  validateDef,
} from '../../lib/outreach/customFields'
import type { CustomFieldDef, CustomFieldEntity, CustomFieldType } from '../../lib/outreach/types'
import { toast } from '../../lib/toast'
import { Badge, Button, EmptyState } from '../../shared/ui'

/**
 * Settings manager for custom field definitions. Fields belong to the active
 * business; values show up in the contact and account drawers, can be mapped
 * from CSV columns, used in saved view filters and as {{custom.<key>}} merge
 * fields in sequences. The key is fixed after creation because values are
 * stored under it.
 */

interface Draft {
  id: string | null
  entity: CustomFieldEntity
  key: string
  keyTouched: boolean
  label: string
  type: CustomFieldType
  optionsText: string
}

const EMPTY_DRAFT = (entity: CustomFieldEntity): Draft => ({
  id: null,
  entity,
  key: '',
  keyTouched: false,
  label: '',
  type: 'text',
  optionsText: '',
})

const TYPE_LABEL = Object.fromEntries(CUSTOM_FIELD_TYPES.map((t) => [t.value, t.label])) as Record<
  CustomFieldType,
  string
>

export default function CustomFieldsSettings() {
  const [entity, setEntity] = useState<CustomFieldEntity>('contact')
  const [defs, setDefs] = useState<CustomFieldDef[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState('')
  const [canEdit, setCanEdit] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const [rows, role] = await Promise.all([listCustomFieldDefs(), currentRole()])
      setDefs(rows)
      setCanEdit(role === 'owner' || role === 'admin')
      setStatus('ready')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const shown = useMemo(
    () => defs.filter((d) => d.entity === entity).sort((a, b) => a.position - b.position),
    [defs, entity],
  )

  const startNew = () => {
    setFormError('')
    setDraft(EMPTY_DRAFT(entity))
  }

  const startEdit = (d: CustomFieldDef) => {
    setFormError('')
    setDraft({
      id: d.id,
      entity: d.entity,
      key: d.key,
      keyTouched: true,
      label: d.label,
      type: d.type,
      optionsText: d.options.join('\n'),
    })
  }

  const patch = (p: Partial<Draft>) => setDraft((cur) => (cur ? { ...cur, ...p } : cur))

  const onLabel = (label: string) => {
    if (!draft) return
    patch({ label, ...(draft.id || draft.keyTouched ? {} : { key: slugifyKey(label) }) })
  }

  const save = async () => {
    if (!draft) return
    const options = hasOptions(draft.type) ? parseOptions(draft.optionsText) : []
    const others = defs
      .filter((d) => d.entity === draft.entity && d.id !== draft.id)
      .map((d) => d.key)
    const problem = validateDef(
      { entity: draft.entity, key: draft.key, label: draft.label, type: draft.type, options },
      others,
    )
    if (problem) {
      setFormError(problem)
      return
    }
    setBusy(true)
    try {
      const position = draft.id
        ? (defs.find((d) => d.id === draft.id)?.position ?? 0)
        : Math.max(-1, ...defs.filter((d) => d.entity === draft.entity).map((d) => d.position)) + 1
      await saveCustomFieldDef(draft.id, {
        entity: draft.entity,
        key: draft.key,
        label: draft.label.trim(),
        type: draft.type,
        options,
        position,
      })
      toast(draft.id ? 'Field updated' : 'Field added', 'success')
      setDraft(null)
      await load()
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setFormError(msg)
      toast(msg, 'error')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setBusy(true)
    try {
      await deleteCustomFieldDef(id)
      toast('Field removed', 'success')
      setConfirmDelete(null)
      await load()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const move = async (index: number, dir: -1 | 1) => {
    const a = shown[index]
    const b = shown[index + dir]
    if (!a || !b) return
    setBusy(true)
    try {
      // Positions can tie after deletes; renumber the whole list so a swap always changes order.
      const order = shown.map((d) => d.id)
      order[index] = b.id
      order[index + dir] = a.id
      for (let i = 0; i < order.length; i++) {
        const d = shown.find((x) => x.id === order[i])
        if (d && d.position !== i) await saveCustomFieldDef(d.id, { ...d, position: i })
      }
      await load()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (status === 'error') {
    return (
      <EmptyState
        role="alert"
        title="Could not load custom fields"
        message={error}
        cta={{ label: 'Retry', onClick: () => void load() }}
      />
    )
  }

  return (
    <div className="cfs">
      <style>{`
        .cfs-tabs { display: flex; gap: var(--space-2); margin-bottom: var(--space-3); }
        .cfs-tab { background: transparent; border: 1px solid var(--border2); color: var(--text-dim);
          padding: var(--space-1) var(--space-3); border-radius: var(--radius-sm); cursor: pointer; }
        .cfs-tab[aria-selected='true'] { color: var(--accent); border-color: var(--accent); }
        .cfs-row { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-2) 0;
          border-bottom: 1px solid var(--border); }
        .cfs-row-main { flex: 1; min-width: 0; }
        .cfs-key { font-family: var(--font-mono); font-size: var(--text-xs); color: var(--text-muted); }
        .cfs-actions { display: flex; gap: var(--space-2); }
        .cfs-form { margin-top: var(--space-4); padding: var(--space-4); background: var(--bg2);
          border: 1px solid var(--border); border-radius: var(--radius); max-width: 520px; }
        .cfs-error { color: var(--red); font-size: var(--text-sm); margin: var(--space-2) 0; }
      `}</style>

      <div className="cfs-tabs" role="tablist" aria-label="Field belongs to">
        {(['contact', 'account'] as const).map((e) => (
          <button
            key={e}
            type="button"
            role="tab"
            aria-selected={entity === e}
            className="cfs-tab"
            onClick={() => {
              setEntity(e)
              setDraft(null)
            }}
          >
            {e === 'contact' ? 'Contact fields' : 'Account fields'}
          </button>
        ))}
      </div>

      {status === 'loading' && defs.length === 0 ? (
        <div className="settings-hint">Loading fields…</div>
      ) : shown.length === 0 ? (
        <div className="settings-hint">
          No {entity} fields yet.
          {canEdit ? ' Add one to track something the standard columns do not cover.' : ''}
        </div>
      ) : (
        <div>
          {shown.map((d, i) => (
            <div className="cfs-row" key={d.id}>
              <div className="cfs-row-main">
                <div>
                  {d.label} <Badge>{TYPE_LABEL[d.type]}</Badge>
                </div>
                <div className="cfs-key">
                  {`{{custom.${d.key}}}`}
                  {d.options.length > 0 ? ` · ${d.options.join(', ')}` : ''}
                </div>
              </div>
              {canEdit && (
                <div className="cfs-actions">
                  <Button
                    variant="secondary"
                    disabled={busy || i === 0}
                    onClick={() => void move(i, -1)}
                    aria-label={`Move ${d.label} up`}
                  >
                    Up
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={busy || i === shown.length - 1}
                    onClick={() => void move(i, 1)}
                    aria-label={`Move ${d.label} down`}
                  >
                    Down
                  </Button>
                  <Button variant="secondary" onClick={() => startEdit(d)}>
                    Edit
                  </Button>
                  {confirmDelete === d.id ? (
                    <Button variant="danger" disabled={busy} onClick={() => void remove(d.id)}>
                      Confirm remove
                    </Button>
                  ) : (
                    <Button variant="danger" onClick={() => setConfirmDelete(d.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          {confirmDelete && (
            <div className="settings-hint">
              Removing a field hides it. Values already saved on records stay in the database.
            </div>
          )}
        </div>
      )}

      {canEdit && !draft && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Button onClick={startNew}>Add {entity} field</Button>
        </div>
      )}

      {draft && (
        <div className="cfs-form">
          <div className="settings-row">
            <label className="settings-label" htmlFor="cfs-label">
              Name
            </label>
            <input
              id="cfs-label"
              className="settings-input"
              value={draft.label}
              onChange={(e) => onLabel(e.target.value)}
              maxLength={80}
            />
          </div>
          <div className="settings-row">
            <label className="settings-label" htmlFor="cfs-key">
              Key
            </label>
            <div className="settings-hint">
              Used in imports and merge fields as {`{{custom.${draft.key || 'key'}}}`}. Cannot be
              changed later.
            </div>
            <input
              id="cfs-key"
              className="settings-input"
              value={draft.key}
              disabled={draft.id !== null}
              onChange={(e) => patch({ key: e.target.value, keyTouched: true })}
              maxLength={40}
            />
          </div>
          <div className="settings-row">
            <label className="settings-label" htmlFor="cfs-type">
              Type
            </label>
            <select
              id="cfs-type"
              className="form-select"
              value={draft.type}
              onChange={(e) => patch({ type: e.target.value as CustomFieldType })}
            >
              {CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          {hasOptions(draft.type) && (
            <div className="settings-row">
              <label className="settings-label" htmlFor="cfs-options">
                Choices
              </label>
              <div className="settings-hint">One per line.</div>
              <textarea
                id="cfs-options"
                className="settings-input"
                rows={4}
                value={draft.optionsText}
                onChange={(e) => patch({ optionsText: e.target.value })}
              />
            </div>
          )}
          {formError && (
            <div className="cfs-error" role="alert">
              {formError}
            </div>
          )}
          <div className="cfs-actions">
            <Button disabled={busy} onClick={() => void save()}>
              {draft.id ? 'Save field' : 'Add field'}
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
